"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Zap, Shield, Download } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function NextGenDonutsmpUtilities() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="222679" id="lunivers-dhenintsoa" className="bg-background text-foreground relative min-h-[90vh] flex items-center overflow-hidden py-20">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/15 rounded-lg blur-[160px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[30rem] h-[30rem] bg-secondary/15 rounded-lg blur-[180px] pointer-events-none" />
      
      {}
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1646506/img/cyber_grid_overlay.png)] bg-center bg-cover opacity-15 pointer-events-none mix-blend-overlay animate-pulse" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          
          {}
          <div ref={leftRef} className={`flex flex-col items-start space-y-8 transition-all duration-1000 ease-out ${isLeftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="inline-flex items-center space-x-2 bg-primary/5 border border-primary/20 px-4 py-2 rounded-lg backdrop-blur-md">
              <Zap className="w-5 h-5 text-primary animate-pulse" />
              <span className="font-mono text-primary font-bold tracking-widest uppercase text-sm">
                Digital Architect & CEO
              </span>
            </div>

            <div className="space-y-4">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold font-default leading-[1.1] tracking-tighter">
                <span className="bg-gradient-to-r from-primary via-primary/80 to-secondary bg-clip-text text-transparent">
                  HENINTSOA
                </span>
                <br />
                <span className="text-foreground">ANDRIANTOMADY</span>
              </h1>
              <p className="text-muted-foreground text-lg sm:text-xl max-w-lg font-default leading-relaxed">
                CEO of <strong className="text-foreground font-semibold">Smilegeeks</strong>. Expert in <strong className="text-primary font-semibold">SEO</strong>, <strong className="text-secondary font-semibold">Marketing Automation</strong>, and <strong className="text-foreground font-semibold">IA</strong> integration.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4 w-full sm:w-auto">
              <div className="relative group">
                <div className="absolute -inset-1 bg-primary/30 blur-xl rounded-lg opacity-0 group-hover:opacity-100 transition duration-500"></div>
                <Button asChild size="lg" className="relative w-full sm:w-auto h-16 px-10 text-lg font-bold tracking-wide rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white backdrop-blur-xl transition-all duration-300">
                  <Link to="#">
                    DISCUTER PROJET
                    <Download className="ml-2 w-6 h-6" />
                  </Link>
                </Button>
              </div>
              
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto h-16 px-8 text-lg font-semibold rounded-lg border-border/50 hover:bg-accent/20 transition-colors">
                <Link to="#">
                  <Shield className="mr-2 w-5 h-5 text-muted-foreground" />
                  Mon Parcours
                </Link>
              </Button>
            </div>
            
            <div className="flex items-center space-x-4 text-sm text-muted-foreground font-mono pt-4">
              <span className="flex items-center">
                <span className="w-2 h-2 rounded-lg bg-primary mr-2 animate-pulse"></span>
                Status: Available for Strategy
              </span>
              <span className="opacity-50">|</span>
              <span>Digital Excellence</span>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`relative flex justify-center items-center h-[500px] lg:h-[600px] transition-all duration-1000 delay-300 ease-out ${isRightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`} style={{
          transform: `translateY(${scrollY * 0.15}px)`
        }}>
            {}
            <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1646506/img/floating_digital_particles.png)] bg-cover bg-center opacity-60 mix-blend-screen animate-[pulse_2s_infinite] pointer-events-none rounded-lg" />
            
            {}
            <div className="absolute w-64 h-64 bg-secondary/30 rounded-lg blur-[100px] animate-pulse" />

            {}
            <div className="relative z-10 w-full max-w-md mx-auto transform transition-all duration-700 hover:scale-105 group animate-[float_3.5s_ease-in-out_infinite]">
              {}
              <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden rounded-lg">
                <div className="absolute inset-0 bg-linear-to-b from-transparent via-primary/5 to-transparent h-[200%] w-full animate-[scanline_4s_linear_infinite]" />
              </div>
              
              {}
              <div className="absolute -inset-1 bg-gradient-to-tr from-primary via-secondary to-primary rounded-lg blur-md opacity-40 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="absolute -inset-4 bg-primary/20 blur-3xl rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              
              <img src="https://xn--hents-5ua.com/wp-content/uploads/2026/05/Gemini_Generated_Image_-2.png" alt="Henintsoa Andriantomady Digital Portrait" className="relative z-10 w-full h-auto object-contain rounded-lg border border-white/10" loading="lazy" decoding="async" width={500} height={600} />
            </div>
            
            {}
            <div className="absolute top-10 right-10 bg-card/40 backdrop-blur-xl border border-white/10 p-3 rounded-lg shadow-2xl animate-[bounce_3s_infinite] hover:animate-[glitch_0.3s_infinite]">
              <span className="font-mono text-xs text-primary font-bold">SMILEGEEKS CEO</span>
            </div>
            <div className="absolute bottom-20 left-10 bg-card/40 backdrop-blur-xl border border-white/10 p-3 rounded-lg shadow-2xl animate-[bounce_4.5s_infinite_reverse] hover:animate-[glitch_0.3s_infinite] delay-700">
              <span className="font-mono text-xs text-secondary font-bold">IA & AUTOMATION</span>
            </div>
          </div>

        </div>
      </div>

      <style jsx>{`
        @keyframes scanline {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        @keyframes glitch {
          0% { transform: translate(0); }
          20% { transform: translate(-2px, 2px); }
          40% { transform: translate(-2px, -2px); }
          60% { transform: translate(2px, 2px); }
          80% { transform: translate(2px, -2px); }
          100% { transform: translate(0); }
        }
      `}</style>
    </section>;
}